// context/AdminContext.js - UPDATED VERSION
import React, { createContext, useContext, useState } from 'react';
import { useUser } from './UserContext';

const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const { user, logout: userLogout, refreshUserData } = useUser();
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState(null);

  const currentUser = user;
  const isAdmin = user?.role === 'admin';

  // ✅ FIXED: Updated apiRequest function to properly handle method parameter
  const apiRequest = async (url, method = 'GET', body = null, queryParams = {}) => {
    const API_BASE = 'https://travel-agency-server-lilac.vercel.app/api';

    // Build URL with query parameters if any
    let fullUrl = `${API_BASE}${url}`;
    if (Object.keys(queryParams).length > 0 && method.toUpperCase() === 'GET') {
      const params = new URLSearchParams(queryParams).toString();
      fullUrl += `?${params}`;
    }

    console.log(`🔄 Admin API Request: ${method} ${fullUrl}`);

    // Get token from localStorage
    const token = localStorage.getItem('userToken');

    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };

    const config = {
      method: method.toUpperCase(),
      headers: headers,
      credentials: 'include'
    };

    // Stringify body if it exists and method is not GET/HEAD
    if (body && method.toUpperCase() !== 'GET' && method.toUpperCase() !== 'HEAD') {
      config.body = JSON.stringify(body);
    }

    try {
      const response = await fetch(fullUrl, config);

      // Handle 401 Unauthorized
      if (response.status === 401) {
        userLogout();
        throw new Error('Authentication failed. Please sign in again.');
      }

      // Handle 404 Not Found
      if (response.status === 404) {
        throw new Error(`Route not found: ${url}`);
      }

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
        }
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      // Check if response has content
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        console.log(`✅ Admin API Success: ${method} ${fullUrl}`);
        return data;
      } else {
        console.log(`✅ Admin API Success (non-JSON): ${method} ${fullUrl}`);
        return { success: true };
      }

    } catch (error) {
      console.error(`🚨 Admin API Request failed for ${fullUrl}:`, error);
      throw error;
    }
  };

  // ✅ EXISTING: Contact Information Management
  const getContactInfo = async () => {
    try {
      const data = await apiRequest('/contact/contact-info');
      return data.contactInfo;
    } catch (error) {
      console.error('Get contact info error:', error);
      throw error;
    }
  };

  const updateContactInfo = async (contactData) => {
    try {
      const data = await apiRequest('/contact/contact-info', 'PUT', contactData);
      return data;
    } catch (error) {
      console.error('Update contact info error:', error);
      throw error;
    }
  };

  // ✅ NEW: Contact Messages Management
  const getContactMessages = async (queryParams = {}) => {
    try {
      const data = await apiRequest('/contact/messages', 'GET', null, queryParams);
      return data;
    } catch (error) {
      console.error('Get contact messages error:', error);
      throw error;
    }
  };

  const getContactMessage = async (id) => {
    try {
      const data = await apiRequest(`/contact/messages/${id}`);
      return data;
    } catch (error) {
      console.error('Get contact message error:', error);
      throw error;
    }
  };

  const updateContactMessageStatus = async (id, status) => {
    try {
      const data = await apiRequest(`/contact/messages/${id}/status`, 'PUT', { status });
      return data;
    } catch (error) {
      console.error('Update message status error:', error);
      throw error;
    }
  };

  const replyToContactMessage = async (id, replyMessage) => {
    try {
      const data = await apiRequest(`/contact/messages/${id}/reply`, 'POST', { replyMessage });
      return data;
    } catch (error) {
      console.error('Reply to message error:', error);
      throw error;
    }
  };

  const deleteContactMessage = async (id) => {
    try {
      const data = await apiRequest(`/contact/messages/${id}`, 'DELETE');
      return data;
    } catch (error) {
      console.error('Delete message error:', error);
      throw error;
    }
  };

  const getContactMessagesStats = async () => {
    try {
      const data = await apiRequest('/contact/messages/stats');
      return data;
    } catch (error) {
      console.error('Get messages stats error:', error);
      throw error;
    }
  };

  // ✅ EXISTING: Admin management functions
  const getAdminRequests = async () => {
    try {
      const data = await apiRequest('/auth/admin-requests');
      return data.requests || [];
    } catch (error) {
      console.error('Get admin requests error:', error);
      throw error;
    }
  };

  const approveAdminRequest = async (userId) => {
    try {
      console.log(`🔄 Approving admin request for user: ${userId}`);
      const data = await apiRequest(`/auth/approve-admin/${userId}`, 'POST');

      if (data.success) {
        console.log('✅ Admin approved successfully');

        if (user?._id === userId) {
          await refreshUserData();
        }
      }

      return data;
    } catch (error) {
      console.error('❌ Approve admin request error:', error);
      throw error;
    }
  };

  const cancelAdminRequest = async (userId) => {
    try {
      console.log(`🔄 Cancelling admin request for user: ${userId}`);
      const data = await apiRequest(`/auth/cancel-admin-request/${userId}`, 'POST');

      if (data.success) {
        console.log('✅ Admin request cancelled successfully');

        if (user?._id === userId) {
          await refreshUserData();
        }
      }

      return data;
    } catch (error) {
      console.error('❌ Cancel admin request error:', error);
      throw error;
    }
  };

  const getAllAdmins = async () => {
    try {
      const data = await apiRequest('/auth/admins');
      return data.admins || [];
    } catch (error) {
      console.error('Get all admins error:', error);
      throw error;
    }
  };

  const removeAdmin = async (userId) => {
    try {
      console.log(`🔄 Removing admin for user ID: ${userId}`);
      const data = await apiRequest(`/auth/remove-admin/${userId}`, 'POST');

      if (data.success) {
        console.log('✅ Admin privileges removed successfully');

        if (user?._id === userId) {
          await refreshUserData();
        }
      }

      return data;
    } catch (error) {
      console.error(`🚨 Remove admin failed for user ${userId}:`, error);
      throw error;
    }
  };

  // ✅ EXISTING: Package management functions
  const getPackages = async () => {
    try {
      const data = await apiRequest('/packages');
      return data.packages || [];
    } catch (error) {
      console.error('Get packages error:', error);
      throw error;
    }
  };

  const addPackage = async (packageData) => {
    try {
      const data = await apiRequest('/packages', 'POST', packageData);
      return data;
    } catch (error) {
      console.error('Add package error:', error);
      throw error;
    }
  };

  const updatePackage = async (packageId, packageData) => {
    try {
      const data = await apiRequest(`/packages/${packageId}`, 'PUT', packageData);
      return data;
    } catch (error) {
      console.error('Update package error:', error);
      throw error;
    }
  };

  const deletePackage = async (packageId) => {
    try {
      const data = await apiRequest(`/packages/${packageId}`, 'DELETE');
      return data;
    } catch (error) {
      console.error('Delete package error:', error);
      throw error;
    }
  };

  const updatePackagePrice = async (packageId, newPrice) => {
    try {
      return await updatePackage(packageId, { price: newPrice });
    } catch (error) {
      console.error('Update package price error:', error);
      throw error;
    }
  };

  // ✅ EXISTING: Custom package requests management
  const getCustomPackageRequests = async () => {
    try {
      const data = await apiRequest('/custom-packages/requests');
      return data.requests || [];
    } catch (error) {
      console.error('Get custom package requests error:', error);
      throw error;
    }
  };

  const updateCustomPackageRequest = async (requestId, updateData) => {
    try {
      const data = await apiRequest(`/custom-packages/requests/${requestId}`, 'PUT', updateData);
      return data;
    } catch (error) {
      console.error('Update custom package request error:', error);
      throw error;
    }
  };

  // ✅ EXISTING: Destination Gallery Management functions
  const getDestinationGalleryItems = async (queryParams = {}) => {
    try {
      let url = '/destination-gallery/admin/all';
      if (Object.keys(queryParams).length > 0) {
        const params = new URLSearchParams(queryParams).toString();
        url += `?${params}`;
      }
      const data = await apiRequest(url);
      return data;
    } catch (error) {
      console.error('Get destination gallery items error:', error);
      throw error;
    }
  };

  const createDestinationGalleryItem = async (itemData, force = false) => {
    try {
      let url = '/destination-gallery/admin/create';
      if (force) {
        url += '?force=true';
      }
      const data = await apiRequest(url, 'POST', itemData);
      return data;
    } catch (error) {
      console.error('Create destination gallery item error:', error);
      throw error;
    }
  };

  const updateDestinationGalleryItem = async (id, itemData, force = false) => {
    try {
      let url = `/destination-gallery/admin/update/${id}`;
      if (force) {
        url += '?force=true';
      }
      const data = await apiRequest(url, 'PUT', itemData);
      return data;
    } catch (error) {
      console.error('Update destination gallery item error:', error);
      throw error;
    }
  };

  const deleteDestinationGalleryItem = async (id) => {
    try {
      const data = await apiRequest(`/destination-gallery/admin/delete/${id}`, 'DELETE');
      return data;
    } catch (error) {
      console.error('Delete destination gallery item error:', error);
      throw error;
    }
  };

  // AdminContext.js - নতুন functions যোগ করুন

  // ✅ Booking Management Functions
  const getBookings = async (queryParams = {}) => {
    try {
      const data = await apiRequest('/bookings', 'GET', null, queryParams);
      return data;
    } catch (error) {
      console.error('Get bookings error:', error);
      throw error;
    }
  };

  const getBooking = async (id) => {
    try {
      const data = await apiRequest(`/bookings/${id}`);
      return data;
    } catch (error) {
      console.error('Get booking error:', error);
      throw error;
    }
  };

  const updateBookingStatus = async (id, status, adminNotes = '') => {
    try {
      const data = await apiRequest(`/bookings/${id}/status`, 'PUT', {
        status,
        adminNotes
      });
      return data;
    } catch (error) {
      console.error('Update booking status error:', error);
      throw error;
    }
  };

  const deleteBooking = async (id) => {
    try {
      const data = await apiRequest(`/bookings/${id}`, 'DELETE');
      return data;
    } catch (error) {
      console.error('Delete booking error:', error);
      throw error;
    }
  };

  const getBookingStats = async () => {
    try {
      const data = await apiRequest('/bookings/stats/overview');
      return data;
    } catch (error) {
      console.error('Get booking stats error:', error);
      throw error;
    }
  };

  const logout = userLogout;

  const value = {
    currentUser,
    isAdmin,
    loading,
    authError,
    logout,
    // ✅ NEW: Contact Messages Management
    getContactMessages,
    getContactMessage,
    updateContactMessageStatus,
    replyToContactMessage,
    deleteContactMessage,
    getContactMessagesStats,
    // ✅ EXISTING: Contact Information
    getContactInfo,
    updateContactInfo,
    // ✅ EXISTING: Admin management
    getAdminRequests,
    cancelAdminRequest,
    approveAdminRequest,
    getAllAdmins,
    removeAdmin,
    // ✅ EXISTING: Package management
    getPackages,
    addPackage,
    updatePackage,
    deletePackage,
    updatePackagePrice,
    // ✅ EXISTING: Custom packages
    getCustomPackageRequests,
    updateCustomPackageRequest,
    // ✅ EXISTING: Destination Gallery Management
    getDestinationGalleryItems,
    createDestinationGalleryItem,
    updateDestinationGalleryItem,
    deleteDestinationGalleryItem,
    // ✅ EXISTING: Raw apiRequest function
    apiRequest,

    // ✅ Booking Management
    getBookings,
    getBooking,
    updateBookingStatus,
    deleteBooking,
    getBookingStats,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};